import { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, PlayCircle, CheckCircle, AlertTriangle, XCircle, Server } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useOutletContext } from 'react-router-dom';
import { getSystemStatus, triggerSync, triggerCrawl } from '../../utils/api';
import type { SystemStatus } from '../../utils/types';

interface AdminContext {
  secret: string;
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return parts.join(' ');
}

function formatDatetime(dateStr: string | null): string {
  if (!dateStr) return 'Ποτέ';
  try {
    return new Intl.DateTimeFormat('el-GR', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

export default function AdminSystem() {
  const { secret } = useOutletContext<AdminContext>();
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCrawling, setIsCrawling] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    getSystemStatus(secret)
      .then(setStatus)
      .finally(() => setIsLoading(false));
  }, [secret]);

  useEffect(() => { load(); }, [load]);

  const msgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (msgTimerRef.current) clearTimeout(msgTimerRef.current); }, []);
  const showMsg = (msg: string) => {
    setMessage(msg);
    if (msgTimerRef.current) clearTimeout(msgTimerRef.current);
    msgTimerRef.current = setTimeout(() => setMessage(null), 3000);
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await triggerSync(secret);
      showMsg('Συγχρονισμός ξεκίνησε');
      load();
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCrawl = async () => {
    setIsCrawling(true);
    try {
      await triggerCrawl(secret);
      showMsg('Ανίχνευση ξεκίνησε');
      load();
    } finally {
      setIsCrawling(false);
    }
  };

  const healthColor =
    status?.health === 'healthy'
      ? 'text-success border-success/30 bg-success/5'
      : status?.health === 'degraded'
        ? 'text-warning border-warning/30 bg-warning/5'
        : 'text-destructive border-destructive/30 bg-destructive/5';

  const HealthIcon =
    status?.health === 'healthy' ? CheckCircle
      : status?.health === 'degraded' ? AlertTriangle
        : XCircle;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">System Status</h1>
          <p className="text-muted-foreground">Monitor and control the search engine</p>
        </div>
        <div className="flex items-center gap-2">
          {message && <span className="text-sm text-success font-medium">{message}</span>}
          <Button variant="outline" size="sm" onClick={load} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
            Ανανέωση
          </Button>
        </div>
      </div>

      {status && (
        <>
          {/* Health */}
          <Card className={`border ${healthColor}`}>
            <CardContent className="flex items-center gap-3 p-5">
              <HealthIcon className="h-6 w-6" />
              <div>
                <p className="font-semibold capitalize">{status.health}</p>
                <p className="text-sm opacity-75">Uptime: {formatUptime(status.uptime)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Index Size', value: status.indexSize.toLocaleString('el-GR') },
              { label: 'Products', value: status.productCount.toLocaleString('el-GR') },
              { label: 'Articles', value: status.articleCount.toLocaleString('el-GR') },
              { label: 'Last Sync', value: formatDatetime(status.lastSync) },
            ].map((stat) => (
              <Card key={stat.label}>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{stat.label}</p>
                  <p className="text-xl font-bold">{stat.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-primary" />
              Συγχρονισμός Δεδομένων
            </CardTitle>
            <CardDescription>Συγχρονισμός προϊόντων από feeds</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleSync} disabled={isSyncing} className="w-full">
              {isSyncing ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Συγχρονισμός...</>
              ) : (
                <><PlayCircle className="h-4 w-4 mr-2" /> Εκκίνηση Συγχρονισμού</>
              )}
            </Button>
            {status?.lastSync && (
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Τελευταίος: {formatDatetime(status.lastSync)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="h-4 w-4 text-primary" />
              Ανίχνευση Ιστοσελίδας
            </CardTitle>
            <CardDescription>Crawl botilia.gr για νέο περιεχόμενο</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleCrawl} disabled={isCrawling} className="w-full">
              {isCrawling ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Ανίχνευση...</>
              ) : (
                <><PlayCircle className="h-4 w-4 mr-2" /> Εκκίνηση Ανίχνευσης</>
              )}
            </Button>
            {status?.lastCrawl && (
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Τελευταία: {formatDatetime(status.lastCrawl)}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
